import { Injectable } from '@angular/core';

export type MenuWatchdogRecoveryLevel = 'reread-cache' | 'reload-api';

export interface MenuWatchdogConfig {
  getMenuItemsCount: () => number;
  isLoading: () => boolean;
  hasLocalStorageData: () => boolean;
  countDomMenuItems: () => number;
  onRecovery: (level: MenuWatchdogRecoveryLevel) => void;
}

type StopReason = 'healthy' | 'max_attempts' | 'destroyed';

@Injectable({
  providedIn: 'root',
})
export class MenuWatchdogService {
  private static readonly GRACE_PERIOD_MS = 8_000;
  private static readonly CHECK_INTERVAL_MS = 10_000;
  private static readonly MAX_RECOVERY_ATTEMPTS = 3;
  private static readonly RECOVERY_WINDOW_MS = 5 * 60_000;
  private static readonly RECOVERY_COOLDOWN_MS = 8_000;
  /** Ticks consecutivos en estado healthy antes de auto-stop. */
  private static readonly HEALTHY_TICKS_TO_STOP = 2;

  private config: MenuWatchdogConfig | null = null;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private graceTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private startedAt = 0;
  private graceEndsAt = 0;
  private recoveryAttempts = 0;
  private recoveryWindowStartAt = 0;
  private lastRecoveryAt = 0;
  private consecutiveHealthyTicks = 0;

  start(config: MenuWatchdogConfig): void {
    this.stop('destroyed');
    this.config = config;
    this.startedAt = Date.now();
    this.graceEndsAt = this.startedAt + MenuWatchdogService.GRACE_PERIOD_MS;
    this.recoveryAttempts = 0;
    this.recoveryWindowStartAt = 0;
    this.lastRecoveryAt = 0;
    this.consecutiveHealthyTicks = 0;

    this.graceTimeoutId = setTimeout(() => {
      this.graceTimeoutId = null;
      this.tick();
      this.intervalId = setInterval(() => this.tick(), MenuWatchdogService.CHECK_INTERVAL_MS);
    }, MenuWatchdogService.GRACE_PERIOD_MS);
  }

  stop(reason: StopReason = 'destroyed'): void {
    if (this.intervalId != null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.graceTimeoutId != null) {
      clearTimeout(this.graceTimeoutId);
      this.graceTimeoutId = null;
    }
    if (this.config != null && reason !== 'destroyed') {
      console.warn('[menu-watchdog] Detenido.', { reason });
    }
    this.config = null;
    this.consecutiveHealthyTicks = 0;
  }

  private tick(): void {
    const cfg = this.config;
    if (!cfg) {
      return;
    }

    if (Date.now() < this.graceEndsAt) {
      return;
    }

    if (cfg.isLoading()) {
      return;
    }

    const menuItemsCount = cfg.getMenuItemsCount();
    const domItemsCount = cfg.countDomMenuItems();
    const hasCache = cfg.hasLocalStorageData();

    if (menuItemsCount > 0 && domItemsCount > 0) {
      this.consecutiveHealthyTicks += 1;
      if (this.consecutiveHealthyTicks >= MenuWatchdogService.HEALTHY_TICKS_TO_STOP) {
        this.stop('healthy');
      }
      return;
    }

    this.consecutiveHealthyTicks = 0;

    if (!hasCache) {
      return;
    }

    if (!this.canAttemptRecovery()) {
      this.stop('max_attempts');
      return;
    }

    if (!this.isCooldownElapsed()) {
      return;
    }

    const attemptNumber = this.recoveryAttempts + 1;
    const level: MenuWatchdogRecoveryLevel =
      attemptNumber <= 1 ? 'reread-cache' : 'reload-api';

    console.warn('[menu-watchdog] Anomalía detectada: hay caché local pero el menú no se muestra.', {
      menuItemsCount,
      domItemsCount,
      isLoading: cfg.isLoading(),
      hasCache,
      recoveryLevel: level,
      attemptNumber,
    });

    this.recordRecoveryAttempt();
    cfg.onRecovery(level);
  }

  private canAttemptRecovery(): boolean {
    const now = Date.now();
    if (
      this.recoveryWindowStartAt === 0 ||
      now - this.recoveryWindowStartAt > MenuWatchdogService.RECOVERY_WINDOW_MS
    ) {
      this.recoveryWindowStartAt = now;
      this.recoveryAttempts = 0;
    }
    return this.recoveryAttempts < MenuWatchdogService.MAX_RECOVERY_ATTEMPTS;
  }

  private isCooldownElapsed(): boolean {
    if (this.lastRecoveryAt === 0) {
      return true;
    }
    return Date.now() - this.lastRecoveryAt >= MenuWatchdogService.RECOVERY_COOLDOWN_MS;
  }

  private recordRecoveryAttempt(): void {
    const now = Date.now();
    if (
      this.recoveryWindowStartAt === 0 ||
      now - this.recoveryWindowStartAt > MenuWatchdogService.RECOVERY_WINDOW_MS
    ) {
      this.recoveryWindowStartAt = now;
      this.recoveryAttempts = 0;
    }
    this.recoveryAttempts += 1;
    this.lastRecoveryAt = now;
  }
}
